import { DomUtils, DeviceUtils } from './utils';

class Scrolling {
    constructor() {
        this.DOM = {
            body: document.body,
            anchorLinks: document.querySelectorAll('a[href*="#"]'),
            siteHeader: document.querySelector('.site-header')
        };

        gsap.registerPlugin(ScrollTrigger, Draggable);

        this.scrollSmoother = null;
        this.scrollDraggable = null;

        this.isMobileDevice = DeviceUtils.isMobileDevice();
        this.viewportWidth = DomUtils.getViewportWidth();
        this.viewportHeight = DomUtils.getViewportHeight();
        this.vw = this.viewportWidth / 100;
        this.vh = this.viewportHeight / 100;
        this.scrolledOffset = 40;

        this.externalScrollEventDispatched = false;

        if (this.isMobileDevice) {
            this.DOM.body.classList.add('mobile-device');
        }

        this.setEventListeners();
        this.setScrollRestoration();
        this.setWindowResize();

        setTimeout(() => {
            this.getAdminBar();
        }, 500);

        const enableSmoothScroll = !this.isMobileDevice;
        if (enableSmoothScroll) {
            this.setSmoothScroll();
            this.createCustomScrollbar();
        } else {
            this.setNativeScroll();
        }
    }

    setSmoothScroll() {
        this.scrollSmoother = new Lenis({});

        this.scrollSmoother.on('scroll', ScrollTrigger.update);

        gsap.ticker.add((time) => {
            this.scrollSmoother.raf(time * 1000);
        });

        gsap.ticker.lagSmoothing(0);

        // Refresh to be sure
        setTimeout(() => {
            document.dispatchEvent(new CustomEvent('refreshScrollTrigger'));
        }, 3000);

        ScrollTrigger.create({
            // markers: true,
            trigger: 'html',
            start: `top -${this.scrolledOffset}`,
            onEnter: () => {
                this.setStatusScrolled(true);
            },
            onLeaveBack: () => {
                this.setStatusScrolled(false);
            }
        });
    }

    // Guiding other js components when to rerender things based on scrolling
    dispatchExternalScrollEvent(scrollY) {
        if (!this.externalScrollEventDispatched) {
            this.externalScrollEventDispatched = true;
            setTimeout(() => {
                document.dispatchEvent(
                    new CustomEvent('onUpdateScroll', {
                        detail: { scrollY: scrollY }
                    })
                );
                this.externalScrollEventDispatched = false;
            }, 500);
        }
    }

    setNativeScroll() {
        let lastKnownScrollPosition = 0;
        let ticking = false;

        this.DOM.body.classList.add('native-scroll');

        document.addEventListener('scroll', () => {
            lastKnownScrollPosition = window.scrollY;

            if (!ticking) {
                window.requestAnimationFrame(() => {
                    this.onScrollNative(lastKnownScrollPosition);
                    ticking = false;
                });

                ticking = true;
            }
        });
    }

    setEventListeners() {
        // Example CustomEvent
        // document.dispatchEvent(new CustomEvent('refreshScrollTrigger'));
        document.addEventListener('refreshScrollTrigger', () => {
            // You'll need to call ScrollTrigger.refresh() when the content is resized. ScrollSmoother is built on top of ScrollTrigger.
            console.log('refreshScrollTrigger');
            ScrollTrigger.refresh();
        });

        this.DOM.anchorLinks.forEach((link) => {
            link.addEventListener('click', (event) => {
                this.onAnchorLinkClick(event);
            });
        });
    }

    getAdminBar() {
        this.DOM.adminBar = document.getElementById('wpadminbar');
        this.getAnchorFromURL();
    }

    onAnchorLinkClick(event) {
        const newURL = event.target.href;
        const newURLBase = event.target.href.split('#')[0];
        const currentURL = window.location.href.split('#')[0];
        const isCurrentPage = newURLBase.includes(currentURL) && currentURL.includes(newURLBase);

        if (!isCurrentPage) {
            return;
        }

        event.preventDefault();
        const anchor = newURL.split('#')[1];
        if (!!anchor) {
            this.scrollToAnchor(anchor);
        }
    }

    getAnchorFromURL() {
        const currentUrl = document.URL;
        let anchor = currentUrl.split('#')[1];

        if (!anchor) {
            return;
        }

        setTimeout(() => {
            anchor = anchor.toLowerCase();
            this.scrollToAnchor(anchor);
        }, 200);
    }

    scrollToAnchor(anchor) {
        console.log('scroll to anchor :', anchor);

        const anchorElement = document.querySelector(`#${anchor}`);
        if (!anchorElement) {
            console.log('anchor element not found by id :', anchor);
            return;
        }

        const headerHeight = this.DOM.siteHeader.offsetHeight;
        const anchorElementTop = anchorElement.getBoundingClientRect().top + document.documentElement.scrollTop;
        let scrollPosY = anchorElementTop - headerHeight;

        if (this.DOM.adminBar) {
            const adminBarHeight = this.DOM.adminBar.offsetHeight;
            scrollPosY = scrollPosY - adminBarHeight;
        }

        window.scrollTo({
            top: scrollPosY,
            behavior: 'smooth'
        });
    }

    setWindowResize() {
        let resizeTimer;
        window.onresize = () => {
            clearTimeout(resizeTimer);
            resizeTimer = setTimeout(() => {
                ScrollTrigger.refresh();
            }, 1000);
        };
    }

    onScrollNative(offsetY) {
        this.setStatusScrolled(offsetY > this.scrolledOffset);
    }

    setStatusScrolled(scrolled) {
        this.DOM.body.classList.toggle('scrolled', scrolled);
    }

    setScrollRestoration() {
        if ('scrollRestoration' in history) {
            history.scrollRestoration = 'manual';
        }
    }

    createCustomScrollbar() {
        const scrollWrapper = document.querySelector('.scrollbar-wrapper');
        const scrollBar = document.querySelector('.scrollbar-bar');
        const body = document.body;
        const html = document.documentElement;

        // Calculate total height of the document
        const documentHeight = Math.max(
            body.scrollHeight,
            body.offsetHeight,
            html.clientHeight,
            html.scrollHeight,
            html.offsetHeight
        );

        let scrollLimit = documentHeight;

        // Calculate scrollbar height
        const viewportHeight = window.innerHeight;
        let scrollbarHeight = (viewportHeight / documentHeight) * viewportHeight;
        scrollBar.style.height = `${scrollbarHeight}px`;

        // Update scroll limit on scroll events
        this.scrollSmoother.on('scroll', (e) => {
            scrollLimit = e.limit;
        });

        // GSAP animation for scrollbar
        const scrollTween = gsap.to(scrollBar, {
            y: viewportHeight - scrollbarHeight,
            ease: 'none',
            scrollTrigger: {
                start: 0,
                end: 'max',
                scrub: true
            }
        });

        // Draggable scrollbar setup
        this.scrollDraggable = Draggable.create('.scrollbar-bar', {
            type: 'y',
            bounds: scrollWrapper,
            inertia: true,
            onPress() {
                scrollTween.scrollTrigger.disable(false);
            },
            onDrag() {
                const progress = gsap.utils.normalize(this.minY, this.maxY, this.y);
                const targetScroll = scrollLimit * progress;
                window.scrollObject.scrollSmoother.scrollTo(targetScroll);
            },
            onRelease() {
                const progress = gsap.utils.normalize(this.minY, this.maxY, this.y);
                scrollTween.scrollTrigger.enable();
                scrollTween.progress(progress);
            }
        });

        // Click event on scroll wrapper
        scrollWrapper.addEventListener('click', (e) => {
            if (e.target !== scrollWrapper) {
                return;
            }
            const rect = e.target.getBoundingClientRect();
            const mouseY = e.clientY - rect.top;
            const progress = gsap.utils.normalize(this.scrollDraggable[0].minY, this.scrollDraggable[0].maxY, mouseY);
            const targetScroll = scrollLimit * progress;
            this.scrollSmoother.scrollTo(targetScroll);
        });
    }
}

window.scrollObject = new Scrolling();

// Scrolling for video recordings
// const scrollPoints = [
//     { name: 'stap1', position: 100, duration: 1, delay: 1000 },
//     { name: 'stap2', position: 300, duration: 2, delay: 2000 }
// ];

// let scrollDelay = 0;
// scrollPoints.forEach((point, index) => {
//     const delay = scrollDelay + point.delay;

//     setTimeout(() => {
//         console.log(`${index + 1}: ${point.name}`);
//         gsap.to(window.scrollObject.scrollSmoother, {
//             scrollTop: point.position,
//             duration: point.duration,
//             ease: 'power3.inOut'
//         });
//     }, delay);

//     scrollDelay = delay;
// });
